/**
 * LongPressRecordButton 测试页面
 * 测试改造后的长按录音按钮，包括桌面端保持模式和时长限制功能
 */
import React, { useState, useCallback } from 'react';
import { VStack, Text, Box, HStack, Switch, FormControl, FormLabel } from '@chakra-ui/react';
import { LongPressRecordButton } from './LongPressRecordButton';
import { useFrontendLogger } from '../../../../atoms/v3/debugAtoms';

const LongPressRecordButtonTest: React.FC = () => {
  const logger = useFrontendLogger();
  const [lastRecordingInfo, setLastRecordingInfo] = useState<{
    size: number;
    type: string;
    timestamp: number;
    duration: number;
  } | null>(null);
  const [lastTranscription, setLastTranscription] = useState<string | null>(null);
  const [enableSTT, setEnableSTT] = useState(true);
  const [maxDuration, setMaxDuration] = useState(15);
  const [warningThreshold, setWarningThreshold] = useState(10);

  // 录音完成回调
  const handleRecordingComplete = useCallback((blob: Blob, duration: number) => {
    logger.info('LongPressRecordButtonTest: Recording completed', {
      size: blob.size,
      type: blob.type,
      duration
    });
    
    setLastRecordingInfo({
      size: blob.size,
      type: blob.type,
      timestamp: Date.now(),
      duration
    });
  }, [logger]);

  // 录音取消回调
  const handleRecordingCancel = useCallback(() => {
    logger.info('LongPressRecordButtonTest: Recording cancelled');
    setLastRecordingInfo(null);
  }, [logger]);

  // 录音编辑回调
  const handleRecordingEdit = useCallback((blob: Blob, duration: number) => {
    logger.info('LongPressRecordButtonTest: Recording for edit', {
      size: blob.size,
      type: blob.type,
      duration
    });
    
    setLastRecordingInfo({
      size: blob.size,
      type: blob.type,
      timestamp: Date.now(),
      duration
    });
  }, [logger]);

  // STT转录完成回调
  const handleTranscriptionComplete = useCallback((text: string) => {
    logger.info('LongPressRecordButtonTest: STT转录完成', { text });
    setLastTranscription(text);
  }, [logger]);

  return (
    <VStack spacing={6} p={6} maxW="600px" mx="auto">
      <Text fontSize="2xl" fontWeight="bold">
        LongPressRecordButton 测试
      </Text>
      
      <Text fontSize="md" color="gray.600" textAlign="center">
        测试改造后的录音按钮：
        <br />
        • 移动端：长按录音，支持左右拖拽手势
        <br />
        • 桌面端：点击进入保持模式，无需一直按住
        <br />
        • 支持录音时长限制和提前提醒
      </Text>

      {/* 配置选项 */}
      <VStack spacing={4} w="full" p={4} bg="gray.50" rounded="md">
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="enable-stt" mb="0">
            启用STT转录
          </FormLabel>
          <Switch
            id="enable-stt"
            isChecked={enableSTT}
            onChange={(e) => setEnableSTT(e.target.checked)}
          />
        </FormControl>

        <HStack w="full">
          <FormControl>
            <FormLabel>最大录音时长（秒）</FormLabel>
            <input
              type="number"
              value={maxDuration}
              onChange={(e) => setMaxDuration(Number(e.target.value))}
              min={5}
              max={60}
              className="w-full p-2 border rounded"
            />
          </FormControl>
          
          <FormControl>
            <FormLabel>提醒阈值（秒）</FormLabel>
            <input
              type="number"
              value={warningThreshold}
              onChange={(e) => setWarningThreshold(Number(e.target.value))}
              min={1}
              max={maxDuration - 1}
              className="w-full p-2 border rounded"
            />
          </FormControl>
        </HStack>
      </VStack>

      {/* 录音按钮 */}
      <Box w="full" maxW="400px">
        <LongPressRecordButton
          onRecordingComplete={handleRecordingComplete}
          onRecordingCancel={handleRecordingCancel}
          onRecordingEdit={handleRecordingEdit}
          onTranscriptionComplete={handleTranscriptionComplete}
          enableSTT={enableSTT}
          maxRecordingDuration={maxDuration}
          warningThreshold={warningThreshold}
          className="w-full"
        />
      </Box>

      {/* 录音信息显示 */}
      {lastRecordingInfo && (
        <Box w="full" p={4} bg="green.50" rounded="md">
          <Text fontWeight="bold" color="green.700" mb={2}>
            最后录音信息：
          </Text>
          <VStack align="start" spacing={1}>
            <Text fontSize="sm">
              大小: {(lastRecordingInfo.size / 1024).toFixed(2)} KB
            </Text>
            <Text fontSize="sm">
              类型: {lastRecordingInfo.type}
            </Text>
            <Text fontSize="sm">
              时长: {(lastRecordingInfo.duration / 1000).toFixed(2)} 秒
            </Text>
            <Text fontSize="sm">
              时间: {new Date(lastRecordingInfo.timestamp).toLocaleTimeString()}
            </Text>
          </VStack>
        </Box>
      )}

      {/* STT转录结果显示 */}
      {lastTranscription && (
        <Box w="full" p={4} bg="blue.50" rounded="md">
          <Text fontWeight="bold" color="blue.700" mb={2}>
            STT转录结果：
          </Text>
          <Text fontSize="sm" color="blue.600">
            {lastTranscription}
          </Text>
        </Box>
      )}

      {/* 使用说明 */}
      <Box w="full" p={4} bg="yellow.50" rounded="md">
        <Text fontWeight="bold" color="yellow.700" mb={2}>
          使用说明：
        </Text>
        <VStack align="start" spacing={1} fontSize="sm" color="yellow.600">
          <Text>• 移动端：长按开始录音，松开发送</Text>
          <Text>• 桌面端：点击开始录音（保持模式），再次点击停止</Text>
          <Text>• 拖拽到左侧取消，拖拽到右侧转编辑</Text>
          <Text>• 录音时长达到设定值会自动停止</Text>
          <Text>• 提前{warningThreshold}秒会显示剩余时间提醒</Text>
        </VStack>
      </Box>
    </VStack>
  );
};

export default LongPressRecordButtonTest;
